import type { ApiErrorBody, Paginated } from '@asrt/types';

let resolvedApiUrl = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000').replace(/\/+$/, '');

/**
 * Live binding: modules that `import { API_URL }` see the resolved value even
 * after `loadApiBase()` replaces it. A deployed bundle reads its API origin from
 * `/api-base.json` at boot so the control-plane host can change without a rebuild.
 */
export { resolvedApiUrl as API_URL };

/** @returns the API origin actually in effect. */
export async function loadApiBase(): Promise<string> {
  try {
    const res = await fetch('/api-base.json', { cache: 'no-store' });
    if (res.ok) {
      const cfg = (await res.json()) as { api?: string };
      if (cfg.api && /^https?:\/\//.test(cfg.api)) resolvedApiUrl = cfg.api.replace(/\/+$/, '');
    }
  } catch {
    /* same-origin file missing — keep the build-time default */
  }
  return resolvedApiUrl;
}
export const DOMAIN_SUFFIX = import.meta.env.VITE_PROJECT_DOMAIN_SUFFIX ?? 'asrtsystems.dev';

/** Error surfacing the API's `{ error: { code, message, requestId } }` envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, requestId?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === 'UNAUTHORIZED';
  }
}

export function asApiError(e: unknown): ApiError | null {
  return e instanceof ApiError ? e : null;
}

export function errorMessage(e: unknown, fallback = 'Request failed'): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

/* ------------------------------------------------------------------ */
/* CSRF                                                                */
/* ------------------------------------------------------------------ */

let memoryCsrf: string | null = (() => {
  try {
    return sessionStorage.getItem('asrt.csrf');
  } catch {
    return null;
  }
})();

/** Auth endpoints return the token in the body; it is kept for this tab. */
export function setCsrfToken(token: string | null | undefined): void {
  if (token === null) {
    memoryCsrf = null;
    try {
      sessionStorage.removeItem('asrt.csrf');
    } catch {
      /* nothing persistent to clean up */
    }
    return;
  }
  if (!token) return;
  memoryCsrf = token;
  try {
    sessionStorage.setItem('asrt.csrf', token);
  } catch {
    /* private mode — the in-memory copy still works */
  }
}

export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)asrt_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : null;
}

export function getCsrfToken(): string | null {
  return readCsrfCookie() ?? memoryCsrf;
}

let csrfBootstrap: Promise<string | null> | null = null;

/**
 * When the console and the API sit on different registrable domains the page
 * cannot read the double-submit cookie, so ask the API for the token bound to
 * this session instead.
 */
export async function ensureCsrfToken(): Promise<string | null> {
  const known = getCsrfToken();
  if (known) return known;
  csrfBootstrap ??= fetch(`${resolvedApiUrl}/v1/auth/csrf`, { credentials: 'include' })
    .then(async (res) => (res.ok ? ((await res.json()) as { csrfToken?: string | null }).csrfToken ?? null : null))
    .then((token) => {
      setCsrfToken(token);
      return token;
    })
    .finally(() => {
      csrfBootstrap = null;
    });
  return csrfBootstrap;
}

/* ------------------------------------------------------------------ */
/* Fetch wrapper                                                       */
/* ------------------------------------------------------------------ */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export function qs(params?: QueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface RequestOptions {
  method?: HttpMethod;
  /** JSON body (serialised automatically). */
  body?: unknown;
  /** Raw body (File/Blob/string) — for storage object uploads. */
  raw?: BodyInit | null;
  formData?: FormData;
  query?: QueryParams;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Response handling: json (default), text, blob (downloads). */
  expect?: 'json' | 'text' | 'blob';
}

async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const url = `${resolvedApiUrl}${path.startsWith('/') ? path : `/${path}`}${qs(opts.query)}`;
  const headers = new Headers(opts.headers);

  const isForm = opts.formData !== undefined;
  const isRaw = opts.raw !== undefined;
  if (!isForm && !isRaw && opts.body !== undefined) headers.set('content-type', 'application/json');

  // CSRF: every mutating request echoes the double-submit token in a header.
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = await ensureCsrfToken();
    if (csrf) headers.set('x-asrt-csrf', csrf);
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      signal: opts.signal,
      body: isForm ? opts.formData : isRaw ? (opts.raw as BodyInit) : opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'NETWORK_ERROR', `Cannot reach the ASRT API at ${resolvedApiUrl}. Is apps/api running?`);
  }

  if (opts.expect === 'blob') {
    if (!res.ok) {
      throw await toApiError(res);
    }
    return (await res.blob()) as unknown as T;
  }

  const text = await res.text();
  let data: unknown;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const body =
      data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)
        ? (data as ApiErrorBody)
        : undefined;
    throw new ApiError(
      res.status,
      body?.error?.code ?? `HTTP_${res.status}`,
      body?.error?.message ?? (res.statusText || `Request failed (${res.status})`),
      body?.error?.requestId,
      body?.error?.details,
    );
  }

  if (opts.expect === 'text') return String(data ?? '') as T;
  return data as T;
}

async function toApiError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as ApiErrorBody;
    return new ApiError(res.status, body.error?.code ?? `HTTP_${res.status}`, body.error?.message ?? res.statusText, body.error?.requestId, body.error?.details);
  } catch {
    return new ApiError(res.status, `HTTP_${res.status}`, res.status ? `Request failed (${res.status})` : 'Request failed');
  }
}

export const api = {
  get: <T>(path: string, query?: QueryParams, opts?: Partial<RequestOptions>) =>
    apiFetch<T>(path, { ...opts, method: 'GET', query }),
  getText: (path: string, query?: QueryParams) => apiFetch<string>(path, { method: 'GET', query, expect: 'text' }),
  getBlob: (path: string, query?: QueryParams) => apiFetch<Blob>(path, { method: 'GET', query, expect: 'blob' }),
  post: <T>(path: string, body?: unknown, query?: QueryParams) => apiFetch<T>(path, { method: 'POST', body, query }),
  put: <T>(path: string, body?: unknown, query?: QueryParams) => apiFetch<T>(path, { method: 'PUT', body, query }),
  patch: <T>(path: string, body?: unknown, query?: QueryParams) => apiFetch<T>(path, { method: 'PATCH', body, query }),
  del: <T>(path: string, query?: QueryParams) => apiFetch<T>(path, { method: 'DELETE', query }),
  /** Raw upload (File/Blob) with a content type. */
  putRaw: <T>(path: string, data: BodyInit, contentType?: string, query?: QueryParams) =>
    apiFetch<T>(path, { method: 'PUT', raw: data, headers: contentType ? { 'content-type': contentType } : undefined, query }),
};

/* ------------------------------------------------------------------ */
/* Pagination helpers                                                  */
/* ------------------------------------------------------------------ */

export const DEFAULT_PAGE_SIZE = 25;

export function pageQuery(page: number, pageSize: number = DEFAULT_PAGE_SIZE, extra?: QueryParams): QueryParams {
  return { page, pageSize, ...extra };
}

export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
}

export async function getPaginated<T>(path: string, page = 1, pageSize = DEFAULT_PAGE_SIZE, extra?: QueryParams): Promise<Paginated<T>> {
  return api.get<Paginated<T>>(path, pageQuery(page, pageSize, extra));
}

export const TERMINAL_DEPLOYMENT_STATUSES = new Set(['ready', 'failed', 'cancelled']);

export function isTerminalDeployment(status: string | null | undefined): boolean {
  return status == null || TERMINAL_DEPLOYMENT_STATUSES.has(status);
}

/** WebSocket URL for /v1/deployments/:id/stream (http -> ws). */
export function wsUrl(path: string): string {
  return `${resolvedApiUrl.replace(/^http/, 'ws')}${path}`;
}
